/**
 * Orthanc REST API wrapper for efficient batch operations
 * Provides methods for fetching changes, series, instances, and lookups
 */

import { IS_OFFLINE_BUILD } from './buildMode';

/**
 * Get the Orthanc server address. Offline: the same-origin proxy of the local
 * launcher. Online: ORTHANC_URL, whose default (the AZmed Orthanc) is set at
 * build time in .webpack/webpack.base.js so that it never reaches the offline
 * bundle.
 * @returns {string} The Orthanc server URL
 */
export const getOrthancAddress = () => {
  if (IS_OFFLINE_BUILD) {
    return `${window.location.origin}/orthanc`;
  }

  return process.env.ORTHANC_URL;
};

/**
 * Orthanc Basic credentials (`user:password`), empty offline. Exposed for the
 * DICOMweb server config, whose `auth` must stay a string: the servers are
 * persisted in sessionStorage, where a function would be dropped.
 * @returns {string} The credentials, '' offline
 */
export const getOrthancCredentials = () => {
  if (IS_OFFLINE_BUILD) {
    return '';
  }

  return `${process.env.ORTHANC_USERNAME}:${process.env.ORTHANC_PASSWORD}`;
};

/**
 * Build the Orthanc authorization headers. Returns a NEW object on every call:
 * some callers add their own headers to it. Offline sends none, the launcher
 * proxy injects its own credentials.
 * @returns {object} `{}` offline, `{ Authorization: 'Basic ...' }` online
 */
export const getOrthancAuthHeaders = () => {
  if (IS_OFFLINE_BUILD) {
    return {};
  }

  return { Authorization: `Basic ${btoa(getOrthancCredentials())}` };
};

/**
 * Set the Orthanc authorization headers on an XHR (no-op offline).
 * @param {XMLHttpRequest} xhr - An opened request
 */
export const applyOrthancAuthHeaders = xhr => {
  const headers = getOrthancAuthHeaders();

  Object.keys(headers).forEach(name =>
    xhr.setRequestHeader(name, headers[name])
  );
};

/**
 * Fetch an Orthanc endpoint with the auth headers applied.
 * @param {string} endpoint - The Orthanc endpoint, e.g. `/studies/${orthancId}/archive`
 * @returns {Promise<Response>} The untouched response
 */
export const fetchOrthanc = async endpoint => {
  return fetch(`${getOrthancAddress()}${endpoint}`, {
    headers: getOrthancAuthHeaders(),
  });
};

/**
 * Fetch all series from Orthanc in a single batch call
 * @param {boolean} expand - Whether to expand series details (default: true)
 * @returns {Promise<Array>} Array of series objects
 */
export const fetchAllSeries = async (expand = true) => {
  try {
    const url = `${getOrthancAddress()}/series${expand ? '?expand' : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch series: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('[orthancAPI] Error fetching all series:', error);
    throw error;
  }
};

/**
 * Lookup Orthanc internal ID from DICOM UID using /tools/lookup
 * @param {string} dicomUID - The DICOM UID to lookup (Study/Series/Instance UID)
 * @returns {Promise<string|null>} The Orthanc internal ID or null if not found
 */
export const lookupOrthancID = async dicomUID => {
  try {
    const url = `${getOrthancAddress()}/tools/lookup`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain',
        ...getOrthancAuthHeaders(),
      },
      body: dicomUID,
    });

    if (!response.ok) {
      console.warn(
        `[orthancAPI] Lookup failed for ${dicomUID}: ${response.status}`
      );

      return null;
    }

    const data = await response.json();

    if (data && data.length > 0 && data[0].ID) {
      return data[0].ID;
    }

    return null;
  } catch (error) {
    console.error('[orthancAPI] Error looking up Orthanc ID:', error);
    return null;
  }
};

/**
 * Fetch a user-defined attachment of a series, as the raw Response.
 *
 * The caller gets every status, including 404, to tell "no attachment" from a
 * failure. Only a network failure rejects.
 *
 * @param {string} seriesOrthancId - The Orthanc internal series ID
 * @param {number} contentType - The user content type, 1024 or above
 * @returns {Promise<Response>} The untouched response
 */
export const fetchSeriesAttachmentResponse = async (
  seriesOrthancId,
  contentType
) => {
  // The content lives under /data. Without it Orthanc answers 200 with the list
  // of sub-resources, which would parse fine and be the wrong thing entirely.
  const url = `${getOrthancAddress()}/series/${seriesOrthancId}/attachments/${contentType}/data`;

  return fetch(url, {
    method: 'GET',
    headers: getOrthancAuthHeaders(),
    // An attachment is mutable: always ask the server, never the HTTP cache.
    cache: 'no-store',
  });
};

/**
 * Fetch the ids of every patient, as the raw Response.
 * @returns {Promise<Response>} The untouched response
 */
export const fetchPatientsResponse = async () => {
  return fetch(`${getOrthancAddress()}/patients`, {
    headers: getOrthancAuthHeaders(),
    // The patient list changes on every import: never the HTTP cache.
    cache: 'no-store',
  });
};

/**
 * Delete several resources at once; Orthanc answers once the deletion is done.
 * @param {string[]} ids - The Orthanc ids of the resources to delete
 * @returns {Promise<Response>} The untouched response
 */
export const bulkDeleteResources = async ids => {
  return fetch(`${getOrthancAddress()}/tools/bulk-delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getOrthancAuthHeaders(),
    },
    body: JSON.stringify({ Resources: ids }),
  });
};

/**
 * Store one DICOM instance.
 * @param {Blob|ArrayBuffer|Uint8Array} body - The DICOM file content
 * @param {AbortSignal} [signal] - Aborts the upload
 * @returns {Promise<Response>} The untouched response
 */
export const postInstance = async (body, signal) => {
  return fetch(`${getOrthancAddress()}/instances`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/dicom',
      ...getOrthancAuthHeaders(),
    },
    body,
    signal,
  });
};

/**
 * Store a JSON user-defined attachment on a series.
 * @param {string} seriesOrthancId - The Orthanc internal series ID
 * @param {number} contentType - The user content type, 1024 or above
 * @param {*} content - The attachment content, serialized as JSON
 * @param {AbortSignal} [signal] - Aborts the upload
 * @returns {Promise<Response>} The untouched response
 */
export const putSeriesAttachment = async (
  seriesOrthancId,
  contentType,
  content,
  signal
) => {
  const url = `${getOrthancAddress()}/series/${seriesOrthancId}/attachments/${contentType}`;

  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getOrthancAuthHeaders(),
    },
    body: JSON.stringify(content),
    signal,
  });
};

/**
 * Fetch all studies from Orthanc in a single batch call, with modalities and
 * protocol name requested
 * @returns {Promise<Array>} Array of expanded study objects
 */
export const fetchAllStudies = async () => {
  try {
    const url = `${getOrthancAddress()}/studies?expand&requestedTags=ModalitiesInStudy%3BProtocolName`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getOrthancAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch studies: ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('[orthancAPI] Error fetching all studies:', error);
    throw error;
  }
};

export default {
  getOrthancAddress,
  getOrthancCredentials,
  getOrthancAuthHeaders,
  applyOrthancAuthHeaders,
  fetchOrthanc,
  fetchAllSeries,
  lookupOrthancID,
  fetchSeriesAttachmentResponse,
  fetchPatientsResponse,
  bulkDeleteResources,
  postInstance,
  putSeriesAttachment,
  fetchAllStudies,
};
